import { type ReactNode } from 'react'
import { useNavigate } from 'react-router'

import { Icon } from './Icon'
import { Image } from './Image'
import { cn } from '../lib/cn'
import { asset } from '../lib/imageAssets'
import type { Tile } from './tileMap'

/**
 * One home service tile (M12-T01, ADR-0026): an icon + label button that routes
 * to the tile's workflow. Emergency tints the icon SOS-red (colour on
 * the icon only, per the spec §2.2); every tile is a real button with an
 * accessible name.
 *
 * M17-T07 — restyled to `FgServiceTile` in `designs/screens-figma-home.jsx`
 * (~103), derived from nodes `295:1187` / `304:1269`. The shape changes from a
 * bordered box with a centred icon to a filled `--accent-bg` image well with
 * the label sitting beneath it, left-aligned.
 *
 * The `tileMap` wiring is untouched, deliberately: which tiles exist, their
 * order and where they route are ADR-0026's, not this milestone's.
 */
export function ServiceTile({ tile }: { tile: Tile }): ReactNode {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      onClick={() => navigate(tile.to)}
      className="group flex w-full flex-col items-start gap-2 rounded-r3 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent sm:gap-4"
    >
      {/* PORTED — image well: --accent-bg, --r-4 (16px), 102 tall.
          M17-T18c fills it with the tile's AMTC photograph where one exists.
          The photograph is decorative: the label directly beneath is the
          accessible name, and it is the button's name too. */}
      <span className="grid h-[102px] w-full place-items-center overflow-hidden rounded-r3 bg-accent-bg transition-[transform,box-shadow] group-hover:-translate-y-0.5 group-hover:shadow-card">
        {tile.image ? (
          <Image
            asset={asset(tile.image)}
            decorative
            className="h-[102px] w-full rounded-r3"
          />
        ) : (
          <Icon
            name={tile.icon}
            // PORTED — 28 compact / 36 desktop. The numeric prop sets the SVG's
            // width/height attributes; the utilities below override them, which
            // is the only way to get a responsive glyph out of this component.
            size={28}
            className={cn(
              'h-7 w-7 sm:h-9 sm:w-9',
              // The SOS tint stays on the icon and only the icon (§2.2). The
              // non-SOS stroke is --accent-ink rather than --accent: upstream
              // paints the glyph with the ink token, and against the accent-bg
              // well the lighter accent would sit too close to its own ground.
              tile.sos ? 'text-sos' : 'text-accent-ink',
            )}
          />
        )}
      </span>
      {/* PORTED — Inter Medium on --ink-2, 14/20 mobile and 18/28 desktop. */}
      <span className="text-[14px] font-medium leading-5 text-ink-2 sm:text-[18px] sm:leading-7">
        {tile.label}
      </span>
    </button>
  )
}
